import { useCallback, useEffect, useMemo, useState } from 'react'
import { collection, collectionGroup, doc, getDoc, onSnapshot, query, where } from 'firebase/firestore'
import { useAuth } from './useAuth.js'
import { db } from '../config/firebase.js'

/**
 * Shared sysadmin status hook with real-time updates - DRY principle
 * Standalone hook for reusability across the app
 */
export const useSysAdminStatus = () => {
  const { user } = useAuth()
  const [isSysAdmin, setIsSysAdmin] = useState(false)
  const userId = user?.uid

  useEffect(() => {
    if (!userId) {
      setIsSysAdmin(false)
      return
    }

    // Real-time listener for sysadmin status changes
    const userRef = doc(db, 'users', userId)
    const unsubscribe = onSnapshot(
      userRef,
      (doc) => {
        const sysAdminStatus = doc.exists() && doc.data().isSysAdmin === true
        setIsSysAdmin(sysAdminStatus)
      },
      (error) => {
        console.error('Error checking sysadmin status:', error)
        setIsSysAdmin(false)
      },
    )

    return unsubscribe
  }, [userId])

  return isSysAdmin
}

/**
 * Shared admin permission checker - DRY principle
 */
const isAdminPermission = (permission) => 
  permission === 'admin' || permission === 'sysadmin'

/**
 * Consolidated Access Data Hook - Firebase v11+ & React 19+ Optimized
 * 
 * Provides real-time data queries for access management operations.
 * Features:
 * - Real-time Firestore listeners with automatic cleanup
 * - Optimized for React 19+ with minimal re-renders
 * - Firebase v11+ SDK compliance
 * - Memoized queries and callbacks
 * 
 * @returns {Object} Access data query functions
 */
export function useAccessData() {
  const { user } = useAuth()

  // Memoized user info fetcher to prevent unnecessary re-creation
  const getUserInfo = useCallback(async (userId) => {
    if (!userId) return null
    
    try {
      const userRef = doc(db, 'users', userId)
      const userDoc = await getDoc(userRef)
      
      if (userDoc.exists()) {
        return { id: userId, ...userDoc.data() }
      }
      
      // Fallback to current user info if available
      if (user?.uid === userId) {
        return {
          id: userId,
          name: user.displayName || 'Unknown User',
          email: user.email || 'No email',
        }
      }
      
      return {
        id: userId,
        name: 'Unknown User',
        email: 'No email',
      }
    } catch (error) {
      console.error('Error fetching user info:', error)
      return {
        id: userId,
        name: 'Unknown User',
        email: 'No email',
      }
    }
  }, [user?.uid, user?.displayName, user?.email])

  // Optimized real-time query hook with stable references
  const useRealtimeQuery = (queryConfig) => {
    const [state, setState] = useState({
      data: [],
      isLoading: true,
      error: null,
    })

    const memoizedQueryConfig = useMemo(() => queryConfig, [queryConfig])

    useEffect(() => {
      if (!memoizedQueryConfig) {
        setState({ data: [], isLoading: false, error: null })
        return
      }

      setState(prev => ({ ...prev, isLoading: true, error: null }))

      const unsubscribe = onSnapshot(
        memoizedQueryConfig,
        (snapshot) => {
          try {
            const results = snapshot.docs.map(doc => {
              const data = doc.data()
              
              // Extract userId from collection group paths
              if (doc.ref.path.includes('userDevices/') && doc.ref.path.includes('/devices/')) {
                const userId = doc.ref.path.split('/')[1]
                return {
                  id: doc.id,
                  userId,
                  deviceId: doc.id,
                  ...data,
                }
              }
              
              return {
                id: doc.id,
                ...data,
              }
            })
            
            setState({ data: results, isLoading: false, error: null })
          } catch (error) {
            console.error('useRealtimeQuery snapshot error:', error)
            setState(prev => ({ ...prev, error, isLoading: false }))
          }
        },
        (error) => {
          setState(prev => ({ ...prev, error, isLoading: false }))
        },
      )

      return unsubscribe
    }, [memoizedQueryConfig])

    return state
  }

  // Reusable permission checker hook
  const useUserPermission = (deviceId) => {
    const [permission, setPermission] = useState(null)
    const userId = user?.uid
    const isSysAdmin = useSysAdminStatus()
    
    useEffect(() => {
      if (!deviceId || !userId) {
        setPermission(null)
        return
      }
      
      // If user is sysadmin, return sysadmin permission immediately
      if (isSysAdmin) {
        setPermission('sysadmin')
        return
      }
      
      // For regular users, check device-specific permissions with real-time listener
      const userDeviceRef = doc(db, `userDevices/${userId}/devices/${deviceId}`)
      const unsubscribe = onSnapshot(
        userDeviceRef,
        (doc) => setPermission(doc.exists() ? doc.data().permissions : null),
        () => setPermission(null),
      )
      
      return unsubscribe
    }, [deviceId, userId, isSysAdmin])
    
    return permission
  }

  // Device access requests hook
  const useDeviceAccessRequests = (deviceId) => {
    const userPermission = useUserPermission(deviceId)
    
    const queryConfig = useMemo(() => {
      return deviceId && isAdminPermission(userPermission)
        ? query(
            collection(db, 'deviceAccessRequests'), 
            where('deviceId', '==', deviceId),
            where('status', '==', 'pending'),
          )
        : null
    }, [deviceId, userPermission])

    return useRealtimeQuery(queryConfig)
  }

  // User access requests hook
  const useUserAccessRequests = () => {
    const userId = user?.uid
    const queryConfig = useMemo(() => {
      return userId
        ? query(collection(db, 'deviceAccessRequests'), where('requestedBy', '==', userId))
        : null
    }, [userId])

    return useRealtimeQuery(queryConfig)
  }

  // Device users hook with optimized data fetching
  const useDeviceUsers = (deviceId) => {
    const [state, setState] = useState({
      data: [],
      isLoading: true,
      error: null,
    })
    const [refreshTrigger, setRefreshTrigger] = useState(0)

    useEffect(() => {
      if (!deviceId) {
        setState({ data: [], isLoading: false, error: null })
        return
      }

      setState(prev => ({ ...prev, isLoading: true, error: null }))

      const collectionGroupQuery = collectionGroup(db, 'devices')
      const unsubscribe = onSnapshot(
        collectionGroupQuery,
        async (snapshot) => {
          try {
            const allDeviceDocs = snapshot.docs
            const relevantDocs = allDeviceDocs.filter(doc => doc.id === deviceId)

            const deviceUserPromises = relevantDocs.map(async (doc) => {
                const data = doc.data()
                const userId = doc.ref.path.split('/')[1]
                
                const [userInfo, addedByInfo] = await Promise.all([
                  getUserInfo(userId),
                  data.addedBy ? getUserInfo(data.addedBy) : null,
                ])
                
                return {
                  id: doc.id,
                  userId,
                  deviceId: doc.id,
                  ...data,
                  userInfo,
                  addedByInfo,
                }
              })
            
            const deviceUsers = await Promise.all(deviceUserPromises)
            
            setState({ data: deviceUsers, isLoading: false, error: null })
          } catch (error) {
            console.error('Error processing device users snapshot:', { deviceId, error })
            setState(prev => ({ ...prev, error, isLoading: false }))
          }
        },
        (error) => {
          console.error('Device users snapshot listener error:', { deviceId, error })
          setState(prev => ({ ...prev, error, isLoading: false }))
        },
      )

      return unsubscribe
    }, [deviceId, refreshTrigger])

    const refresh = () => {
      setRefreshTrigger(prev => prev + 1)
    }

    return { ...state, refresh }
  }

  // User devices hook
  const useUserDevices = () => {
    const userId = user?.uid
    const queryConfig = useMemo(() => {
      return userId ? collection(db, `userDevices/${userId}/devices`) : null
    }, [userId])

    return useRealtimeQuery(queryConfig)
  }

  // Notifications hooks
  const useNotifications = () => {
    const userId = user?.uid
    const queryConfig = useMemo(() => {
      return userId
        ? query(collection(db, 'notifications'), where('userId', '==', userId))
        : null
    }, [userId])

    return useRealtimeQuery(queryConfig)
  }

  const useUnreadNotifications = () => {
    const userId = user?.uid
    const queryConfig = useMemo(() => {
      return userId
        ? query(
            collection(db, 'notifications'), 
            where('userId', '==', userId), 
            where('read', '==', false),
          )
        : null
    }, [userId])

    return useRealtimeQuery(queryConfig)
  }

  // Optimized admin access requests hook
  const useAdminAccessRequests = () => {
    const [state, setState] = useState({
      data: [],
      isLoading: true,
      error: null,
    })

    const { data: userDevices, isLoading: devicesLoading } = useUserDevices()
    const isSysAdmin = useSysAdminStatus()

    useEffect(() => {
      if (devicesLoading) return
      
      setState(prev => ({ ...prev, isLoading: true, error: null }))

      const unsubscribes = []
      const allRequests = new Map()

      if (isSysAdmin) {
        // For sysadmins: Get ALL pending access requests across the system
        const globalQuery = query(
          collection(db, 'deviceAccessRequests'),
          where('status', '==', 'pending'),
        )

        const unsubscribe = onSnapshot(globalQuery,
          (snapshot) => {
            const allAccessRequests = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
            }))
            setState({ data: allAccessRequests, isLoading: false, error: null })
          },
          (error) => {
            console.error('Error fetching sysadmin access requests:', error)
            setState(prev => ({ ...prev, error, isLoading: false }))
          },
        )

        unsubscribes.push(unsubscribe)
      } else {
        // For regular users: Get access requests for devices they admin
        const adminDeviceIds = userDevices
          ?.filter(device => isAdminPermission(device.permissions))
          .map(device => device.id) ?? []

        if (!adminDeviceIds.length) {
          setState({ data: [], isLoading: false, error: null })
          return
        }

        adminDeviceIds.forEach(deviceId => {
          const q = query(
            collection(db, 'deviceAccessRequests'), 
            where('deviceId', '==', deviceId),
            where('status', '==', 'pending'),
          )

          const unsubscribe = onSnapshot(q, 
            (snapshot) => {
              // Update requests for this device
              allRequests.delete(deviceId)
              const deviceRequests = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
              }))
              if (deviceRequests.length) {
                allRequests.set(deviceId, deviceRequests)
              }
              
              // Flatten and update state
              const flatRequests = Array.from(allRequests.values()).flat()
              setState({ data: flatRequests, isLoading: false, error: null })
            },
            (error) => {
              console.error('Error fetching admin access requests for device:', deviceId, error)
              setState(prev => ({ ...prev, isLoading: false }))
            },
          )

          unsubscribes.push(unsubscribe)
        })
      }

      return () => unsubscribes.forEach(unsubscribe => unsubscribe())
    }, [userDevices, devicesLoading, isSysAdmin])

    return state
  }

  // User invitations hook
  const useUserInvitations = () => {
    const userEmail = user?.email
    const queryConfig = useMemo(() => {
      return userEmail
        ? query(
            collection(db, 'deviceInvitations'), 
            where('invitedEmail', '==', userEmail),
            where('status', '==', 'pending'),
          )
        : null
    }, [userEmail])

    return useRealtimeQuery(queryConfig)
  }

  // Device invitations hook
  const useDeviceInvitations = (deviceId) => {
    const userPermission = useUserPermission(deviceId)
    
    const queryConfig = useMemo(() => {
      return deviceId && isAdminPermission(userPermission)
        ? query(
            collection(db, 'deviceInvitations'), 
            where('deviceId', '==', deviceId),
          )
        : null
    }, [deviceId, userPermission])

    return useRealtimeQuery(queryConfig)
  }

  // Return API without memoization to prevent hook dependencies issues
  return {
    // Access requests
    useDeviceAccessRequests,
    useUserAccessRequests,
    useAdminAccessRequests,
    
    // Device users
    useDeviceUsers,
    useUserDevices,
    
    // Notifications
    useNotifications,
    useUnreadNotifications,
    
    // Invitations
    useUserInvitations,
    useDeviceInvitations,
    
    // Utilities
    useRealtimeQuery,
    getUserInfo,
  }
}
